/**
 * Role-Based Access Control Hook
 * 
 * This hook provides role checking and permission management
 * throughout the application.
 * 
 * Usage:
 * const { role, isAdmin, isUser, hasPermission } = useAuth();
 * 
 * if (isAdmin) {
 *   // Show admin-only features
 * }
 */

import { useEffect, useState } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useDispatch, useSelector } from 'react-redux';
import { setUserRole } from '../store/slice/authSlice';

export const useAuth = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const userRole = useSelector(state => state.user.role);
  
  useEffect(() => {
    const fetchRole = async () => {
      try {
        // Fetch user attributes from Cognito
        const attributes = await fetchUserAttributes();
        const role = attributes['custom:role'] || 'user';
        
        // Store role in Redux
        dispatch(setUserRole(role));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setLoading(false);
      }
    };

    if (!userRole) {
      fetchRole();
    } else {
      setLoading(false);
    }
  }, [dispatch, userRole]);

  return {
    role: userRole || 'user',
    isAdmin: userRole === 'admin',
    isUser: userRole === 'user',
    loading,
    hasPermission: (permission) => {
      // Define permission mappings
      const permissions = {
        admin: ['create_user', 'edit_user', 'delete_user', 'view_users', 'manage_properties', 'view_properties'],
        user: ['view_properties']
      };
      
      return permissions[userRole]?.includes(permission) || false;
    }
  };
};

export default useAuth;
